import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useUserRole } from '../../context/UserRoleContext';
import {
  HiChartBar,
  HiCheckCircle,
  HiXCircle,
  HiClock,
  HiArrowRight,
  HiArrowUp,
  HiArrowDown,
} from 'react-icons/hi2';
import { firestore } from '../../database/firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import CommercialisationReadinessChecklist from './CommercialisationReadinessChecklist';
import GTMStrategyPlan from './GTMStrategyPlan';
import StartupLaunchChecklist from './StartupLaunchChecklist';
import { MarbleLoader } from '../ui/LoadingStates';

const CommercialisationDashboard = ({ userRole: propUserRole, userId: propUserId }) => {
  const { currentUser } = useAuth();
  const { userRole: contextUserRole } = useUserRole();
  const [scores, setScores] = useState({
    readiness: null,
    gtm: null,
    launch: null,
  });
  const [loading, setLoading] = useState(true);
  const [nextSteps, setNextSteps] = useState([]);

  const effectiveUserRole = propUserRole || contextUserRole || 'client';
  const effectiveUserId = propUserId || currentUser?.uid;

  // Real-time listeners for all three modules
  useEffect(() => {
    if (!effectiveUserId) {
      setLoading(false);
      return;
    }

    const unsubscribes = [];

    // Readiness Checklist listener
    const readinessRef = doc(
      firestore,
      'commercialisation',
      effectiveUserId,
      'readinessChecklist',
      effectiveUserId
    );
    const unsubscribeReadiness = onSnapshot(
      readinessRef,
      docSnap => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setScores(prev => ({
            ...prev,
            readiness: {
              score: data.score || 0,
              status: data.readinessLevel || 'Not Ready',
              completedItems: data.totalChecked || 0,
              totalItems: data.totalItems || 0,
            },
          }));
        } else {
          setScores(prev => ({ ...prev, readiness: null }));
        }
        setLoading(false);
      },
      error => {
        setLoading(false);
      }
    );
    unsubscribes.push(unsubscribeReadiness);

    // GTM Plan listener
    const gtmRef = doc(firestore, 'commercialisation', effectiveUserId, 'gtmPlan', effectiveUserId);
    const unsubscribeGTM = onSnapshot(
      gtmRef,
      docSnap => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setScores(prev => ({
            ...prev,
            gtm: {
              progress: data.progress || 0,
              sectionsCompleted: data.currentStep || 0,
              totalSections: 15,
              isDraft: data.isDraft !== false,
            },
          }));
        } else {
          setScores(prev => ({ ...prev, gtm: null }));
        }
      },
      error => {
        setLoading(false);
      }
    );
    unsubscribes.push(unsubscribeGTM);

    // Launch Checklist listener
    const launchRef = doc(
      firestore,
      'commercialisation',
      effectiveUserId,
      'launchChecklist',
      effectiveUserId
    );
    const unsubscribeLaunch = onSnapshot(
      launchRef,
      docSnap => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setScores(prev => ({
            ...prev,
            launch: {
              score: data.score || 0,
              status: data.readinessLevel || 'Not Ready',
              completedItems: data.totalChecked || 0,
              totalItems: data.totalItems || 0,
            },
          }));
        } else {
          setScores(prev => ({ ...prev, launch: null }));
        }
      },
      error => {
        setLoading(false);
      }
    );
    unsubscribes.push(unsubscribeLaunch);

    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, [effectiveUserId]);

  // Calculate next steps
  useEffect(() => {
    const steps = [];

    // Readiness checklist suggestions
    if (!scores.readiness || scores.readiness.score < 85) {
      if (scores.readiness?.score < 60) {
        steps.push({
          priority: 'high',
          title: 'Focus on foundational categories',
          description: 'Complete items in Product & Market Fit and Business Model & Revenue',
          action: 'Start Commercialisation Readiness Checklist',
          link: 'commercialisation-readiness-checklist',
        });
      } else if (scores.readiness?.score < 85) {
        steps.push({
          priority: 'medium',
          title: 'Complete remaining readiness items',
          description: `You're almost ready! Complete ${scores.readiness.totalItems - scores.readiness.completedItems} more items to reach 85%`,
          action: 'Continue Commercialisation Readiness Checklist',
          link: 'commercialisation-readiness-checklist',
        });
      }
    }

    // GTM Plan suggestions
    if (!scores.gtm || scores.gtm.progress < 100) {
      steps.push({
        priority: 'medium',
        title: 'Complete Go-to-Market Strategy',
        description: `${scores.gtm ? scores.gtm.progress : 0}% complete. Finish your GTM plan to prepare for market entry`,
        action: 'Continue GTM Strategy Plan',
        link: 'go-to-market-strategy',
      });
    }

    // Launch checklist suggestions
    if (!scores.launch || scores.launch.score < 85) {
      steps.push({
        priority: 'high',
        title: 'Complete Launch Checklist',
        description: `Ensure all launch items are completed before going to market`,
        action: 'Start Launch Checklist',
        link: 'startup-launch-checklist',
      });
    }

    setNextSteps(steps);
  }, [scores]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <MarbleLoader size="md" />
      </div>
    );
  }

  const getScoreColor = score => {
    if (score >= 85) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = score => {
    if (score >= 85) return 'bg-green-600';
    if (score >= 60) return 'bg-yellow-600';
    return 'bg-red-600';
  };

  const getStatusIcon = status => {
    if (status === 'Ready to Commercialise' || status === 'Ready to Launch') {
      return <HiCheckCircle className="h-6 w-6 text-green-600" />;
    }
    if (status === 'Almost Ready') {
      return <HiClock className="h-6 w-6 text-yellow-600" />;
    }
    return <HiXCircle className="h-6 w-6 text-red-600" />;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 md:py-8 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="mb-2 text-3xl font-bold text-black">Commercialisation Dashboard</h1>
          <p className="text-gray-600">Track your readiness for market entry and launch</p>
        </div>

        {/* Score Cards */}
        <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-3">
          {/* Readiness Score */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Commercialisation Readiness</h3>
              {scores.readiness && getStatusIcon(scores.readiness.status)}
            </div>
            {scores.readiness ? (
              <>
                <div className="mb-4">
                  <div className={`text-4xl font-bold ${getScoreColor(scores.readiness.score)}`}>
                    {scores.readiness.score}%
                  </div>
                  <div className="mt-1 text-sm text-gray-600">{scores.readiness.status}</div>
                </div>
                <div className="mb-2 flex items-center justify-between text-sm">
                  <span className="text-gray-600">Progress</span>
                  <span className="font-medium text-gray-900">
                    {scores.readiness.completedItems} / {scores.readiness.totalItems}
                  </span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${scores.readiness.score}%` }}
                    transition={{ duration: 0.5 }}
                    className={`h-full ${getScoreBgColor(scores.readiness.score)}`}
                  />
                </div>
              </>
            ) : (
              <div className="text-center text-gray-500">
                <p className="text-sm">No data available</p>
                <p className="mt-2 text-xs">Start the checklist to track progress</p>
              </div>
            )}
          </motion.div>

          {/* GTM Plan Progress */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Go-to-Market Plan</h3>
              {scores.gtm && (
                <div className="flex items-center gap-1">
                  {scores.gtm.isDraft ? (
                    <HiClock className="h-5 w-5 text-yellow-600" />
                  ) : (
                    <HiCheckCircle className="h-5 w-5 text-green-600" />
                  )}
                </div>
              )}
            </div>
            {scores.gtm ? (
              <>
                <div className="mb-4">
                  <div className={`text-4xl font-bold ${getScoreColor(scores.gtm.progress)}`}>
                    {scores.gtm.progress}%
                  </div>
                  <div className="mt-1 text-sm text-gray-600">
                    {scores.gtm.sectionsCompleted} of {scores.gtm.totalSections} sections
                  </div>
                </div>
                <div className="mb-2 flex items-center justify-between text-sm">
                  <span className="text-gray-600">Progress</span>
                  <span className="font-medium text-gray-900">
                    {scores.gtm.sectionsCompleted} / {scores.gtm.totalSections}
                  </span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${scores.gtm.progress}%` }}
                    transition={{ duration: 0.5 }}
                    className={`h-full ${getScoreBgColor(scores.gtm.progress)}`}
                  />
                </div>
              </>
            ) : (
              <div className="text-center text-gray-500">
                <p className="text-sm">No data available</p>
                <p className="mt-2 text-xs">Start the GTM plan to track progress</p>
              </div>
            )}
          </motion.div>

          {/* Launch Readiness */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Launch Readiness</h3>
              {scores.launch && getStatusIcon(scores.launch.status)}
            </div>
            {scores.launch ? (
              <>
                <div className="mb-4">
                  <div className={`text-4xl font-bold ${getScoreColor(scores.launch.score)}`}>
                    {scores.launch.score}%
                  </div>
                  <div className="mt-1 text-sm text-gray-600">{scores.launch.status}</div>
                </div>
                <div className="mb-2 flex items-center justify-between text-sm">
                  <span className="text-gray-600">Progress</span>
                  <span className="font-medium text-gray-900">
                    {scores.launch.completedItems} / {scores.launch.totalItems}
                  </span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${scores.launch.score}%` }}
                    transition={{ duration: 0.5 }}
                    className={`h-full ${getScoreBgColor(scores.launch.score)}`}
                  />
                </div>
              </>
            ) : (
              <div className="text-center text-gray-500">
                <p className="text-sm">No data available</p>
                <p className="mt-2 text-xs">Start the launch checklist to track progress</p>
              </div>
            )}
          </motion.div>
        </div>

        {/* Next Steps */}
        {nextSteps.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
          >
            <div className="mb-4 flex items-center gap-2">
              <HiArrowUp className="h-5 w-5 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">Next Steps</h2>
            </div>
            <div className="space-y-4">
              {nextSteps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className={`rounded-lg border-l-4 p-4 ${
                    step.priority === 'high'
                      ? 'border-red-500 bg-red-50'
                      : 'border-yellow-500 bg-yellow-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{step.title}</h3>
                      <p className="mt-1 text-sm text-gray-600">{step.description}</p>
                    </div>
                    <button
                      onClick={() => {
                        // Navigate to the checklist (this would be handled by the parent component)
                        window.location.hash = `#${step.link}`;
                      }}
                      className="ml-4 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                    >
                      {step.action}
                      <HiArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Summary View */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
        >
          <div className="mb-4 flex items-center gap-2">
            <HiChartBar className="h-5 w-5 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">Summary</h2>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="text-sm font-medium text-gray-600">Overall Readiness Score</div>
              <div className="mt-2 text-2xl font-bold text-gray-900">
                {scores.readiness?.score || 0}%
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="text-sm font-medium text-gray-600">GTM Plan Progress</div>
              <div className="mt-2 text-2xl font-bold text-gray-900">
                {scores.gtm?.progress || 0}%
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="text-sm font-medium text-gray-600">Launch Readiness</div>
              <div className="mt-2 text-2xl font-bold text-gray-900">
                {scores.launch?.score || 0}%
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CommercialisationDashboard;
